import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { PiggyBank, Lock, Loader2, Check } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export const RetirementPlanner = ({ year, hasPlan, refreshKey, onApplied }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [plan, setPlan] = useState("sep");
  const [age50, setAge50] = useState(false);
  const [amount, setAmount] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const load = (a) => api.get("/vault/taxpal/retirement", { params: { year, plan, age50, ...(a != null ? { contribution: a } : {}) } })
    .then((r) => { setData(r.data); if (a == null) setAmount(r.data.current_contribution || (plan === "solo401k" ? r.data.limits.solo401k_max : r.data.limits.sep_max)); })
    .catch(() => {});
  useEffect(() => { load(); }, [year, plan, age50, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data || !data.has_profile) return null;
  const L = data.limits;
  const max = plan === "solo401k" ? L.solo401k_max : L.sep_max;
  if (L.sep_max <= 0) return null;

  const apply = async () => {
    setBusy(true);
    try {
      await api.post("/vault/taxpal/retirement/apply", null, { params: { year, plan, contribution: amount, age50 } });
      toast.success(amount > 0 ? `${money(amount)} ${plan === "sep" ? "SEP-IRA" : "Solo 401(k)"} contribution added to your estimate` : "Contribution removed from estimate");
      onApplied?.(); load(amount);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not apply"); } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 border border-white/15 rounded-2xl overflow-hidden" data-testid="retirement-planner">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-white/5" data-testid="retirement-toggle">
        <span className="flex items-center gap-2 text-sm font-semibold"><PiggyBank className="h-4 w-4 text-[#7FE3AE]" /> Retirement planner</span>
        {data.sep_at_max && <span className="text-[11px] text-[#7FE3AE]" data-testid="retirement-teaser">Save up to {money(data.sep_at_max.tax_saved)}</span>}
      </button>
      {open && (
        <div className="px-4 pb-4 text-sm">
          <p className="text-[#A7B4CC] text-xs leading-relaxed" data-testid="retirement-headline">
            A SEP-IRA contribution of up to <strong className="text-white">{money(L.sep_max)}</strong> ({L.basis_note}) would cut your {year} tax by about
            <strong className="text-[#7FE3AE]"> {money(data.sep_at_max?.tax_saved)}</strong> — the IRS effectively pays {data.sep_at_max?.savings_rate}% of it.
          </p>
          {!data.unlocked ? (
            <div className="mt-3 bg-white/5 rounded-xl p-3" data-testid="retirement-locked">
              <p className="font-semibold flex items-center gap-2"><Lock className="h-4 w-4 text-[#A7B4CC]" /> Full planner is part of every plan</p>
              <p className="text-xs text-[#A7B4CC] mt-1">Compare SEP-IRA vs Solo 401(k) (up to {money(L.solo401k_max)}), pick your amount, add 50+ catch-up, and fold it into your estimate and quarterly set-aside.</p>
              <Button onClick={() => navigate("/#pricing")} variant="outline" data-testid="retirement-upgrade" className="mt-2 rounded-full border-[#7FE3AE]/60 text-[#7FE3AE] hover:bg-[#7FE3AE]/10 bg-transparent h-8 text-xs">Unlock with a plan</Button>
            </div>
          ) : (
            <div className="mt-3 space-y-3" data-testid="retirement-full">
              <div className="flex gap-1 bg-white/10 rounded-full p-1">
                {[["sep", `SEP-IRA · up to ${money(L.sep_max)}`], ["solo401k", `Solo 401(k) · up to ${money(L.solo401k_max)}`]].map(([k, l]) => (
                  <button key={k} onClick={() => { setPlan(k); setAmount(null); }} data-testid={`retirement-plan-${k}`}
                    className={`flex-1 text-[11px] font-semibold py-1.5 rounded-full ${plan === k ? "bg-white text-[#02112A]" : "text-white/70"}`}>{l}</button>
                ))}
              </div>
              <label className="flex items-center gap-2 text-xs text-[#A7B4CC] cursor-pointer">
                <Checkbox checked={age50} onCheckedChange={(v) => setAge50(!!v)} data-testid="retirement-age50" className="border-white/40 data-[state=checked]:bg-[#7FE3AE] data-[state=checked]:text-[#02112A]" /> I'm 50 or older (adds {money(L.catchup || 8000)} catch-up on Solo 401(k))
              </label>
              <div>
                <div className="flex justify-between text-xs mb-2"><span className="text-[#A7B4CC]">Contribution</span><span className="font-semibold" data-testid="retirement-amount">{money(amount)}</span></div>
                <Slider value={[amount ?? 0]} min={0} max={Math.max(max, 1)} step={250} onValueChange={(v) => setAmount(v[0])} onValueCommit={(v) => load(v[0])} data-testid="retirement-slider" />
              </div>
              {data.scenario && (
                <div className="grid grid-cols-3 gap-2 text-xs" data-testid="retirement-scenario">
                  <div className="bg-white/5 rounded-xl p-2.5"><p className="text-[#A7B4CC]">Tax saved</p><p className="font-semibold text-[#7FE3AE]" data-testid="retirement-saved">{money(data.scenario.tax_saved)}</p></div>
                  <div className="bg-white/5 rounded-xl p-2.5"><p className="text-[#A7B4CC]">Real cost to you</p><p className="font-semibold">{money(data.scenario.effective_cost)}</p></div>
                  <div className="bg-white/5 rounded-xl p-2.5"><p className="text-[#A7B4CC]">Fed / state</p><p className="font-semibold">{money(data.scenario.federal_saved)} / {money(data.scenario.state_saved)}</p></div>
                </div>
              )}
              <Button onClick={apply} disabled={busy} data-testid="retirement-apply" className="w-full rounded-full bg-[#7FE3AE] text-[#02112A] hover:bg-[#9FF0C4] font-semibold h-9">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1.5" /> {data.current_contribution ? "Update my estimate" : "Include in my estimate"}</>}
              </Button>
              {data.current_contribution > 0 && <p className="text-[11px] text-[#A7B4CC]" data-testid="retirement-current">Currently in your estimate: {money(data.current_contribution)} ({data.current_plan === "solo401k" ? "Solo 401(k)" : "SEP-IRA"})</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RetirementPlanner;
